import { useState } from "react";
import { Link } from "react-router-dom";
import { Info, X, ArrowUpRight } from "lucide-react";
import { KPI_REGISTRY } from "@/lib/kpiRegistry";

/**
 * Reusable explainability button + modal.
 * Drop on any KPI card to surface its source, formula, refresh policy.
 *
 * Usage:
 *   <SourceCalcBtn kpiKey="cout_inaction" />
 *
 * The button absolute-positions itself top-right of its closest `relative` parent.
 * Pair with a `relative` wrapper on the KPI card.
 */
export default function SourceCalcBtn({ kpiKey, className = "" }) {
  const [open, setOpen] = useState(false);
  const meta = KPI_REGISTRY[kpiKey];
  if (!meta) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`absolute top-2 right-2 p-1 rounded-md text-zinc-500 hover:text-white hover:bg-white/5 transition-colors z-10 ${className}`}
        title="Source & calcul"
        data-testid={`source-btn-${kpiKey}`}
      >
        <Info className="w-3.5 h-3.5" />
      </button>
      {open && <SourceModal kpiKey={kpiKey} meta={meta} onClose={() => setOpen(false)} />}
    </>
  );
}

function SourceModal({ kpiKey, meta, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()} data-testid={`source-modal-${kpiKey}`}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="noam-overline text-[#378ADD]">Source & calcul</div>
            <div className="noam-display text-base font-semibold mt-1">{meta.title}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/5"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-4 text-sm text-zinc-200">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Sources</div>
            <ul className="space-y-1">
              {meta.sources.map((s, i) => (
                <li key={i} className="text-xs text-zinc-300 font-mono bg-white/[0.03] border border-white/5 rounded px-2 py-1.5">{s}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Formule</div>
            <div className="text-xs text-zinc-300 bg-white/[0.03] border border-white/5 rounded px-3 py-2">{meta.formula}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Rafraîchissement</div>
            <div className="text-xs text-zinc-400">{meta.refresh}</div>
          </div>
          {meta.links && meta.links.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-white/5">
              {meta.links.map((l, i) => (
                <Link key={i} to={l.to} onClick={onClose}
                  className="text-xs px-3 py-1.5 rounded-md border border-[#378ADD]/40 text-[#378ADD] hover:bg-[#378ADD]/10 inline-flex items-center gap-1">
                  {l.label} <ArrowUpRight className="w-3 h-3" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
